/**
 * Policies Page
 * Manage healing policies
 */

import React, { useState, useEffect } from 'react';
import {
  Card,
  Table,
  Typography,
  Space,
  Tag,
  Button,
  Switch,
  Progress,
  Modal,
  Form,
  Input,
  Select,
  Tooltip,
  Row,
  Col,
  Statistic,
  message,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import {
  SafetyCertificateOutlined,
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  BarChartOutlined,


  ThunderboltOutlined,
} from '@ant-design/icons';
import LoadingSpinner from '../components/LoadingSpinner';
import { policyApi } from '../api/hcApi';
import type { Policy, PolicyMode } from '../types';
import dayjs from 'dayjs';

const { Title, Text } = Typography;
const { TextArea } = Input;

const modeConfig: Record<PolicyMode, { color: string; text: string }> = {
  observe: { color: 'default', text: 'Observe' },
  suggest: { color: 'blue', text: 'Suggest' },
  auto: { color: 'green', text: 'Auto' },
};

const Policies: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [policies, setPolicies] = useState<Policy[]>([]);
  const [modalVisible, setModalVisible] = useState(false);
  const [selectedPolicy, setSelectedPolicy] = useState<Policy | null>(null);
  const [statsModalVisible, setStatsModalVisible] = useState(false);
  const [form] = Form.useForm();

  const fetchPolicies = async () => {
    setLoading(true);
    try {
      const response = await policyApi.list();
      if (response.success && response.data) {
        const policyData = Array.isArray(response.data) ? response.data : [];
        setPolicies(policyData);
      } else {
        setPolicies([]);
      }
    } catch (err) {
      console.warn('Failed to fetch policies:', err);
      setPolicies([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPolicies();
  }, []);

  const handleToggleEnabled = (policy: Policy, enabled: boolean) => {
    setPolicies((prev) =>
      prev.map((p) => (p.id === policy.id ? { ...p, enabled } : p))
    );
    message.success(`${policy.name} ${enabled ? 'enabled' : 'disabled'}`);
  };

  const handleEdit = (policy: Policy) => {
    setSelectedPolicy(policy);
    form.setFieldsValue(policy);
    setModalVisible(true);
  };

  const handleDelete = (policy: Policy) => {
    Modal.confirm({
      title: 'Delete Policy',
      content: `Are you sure you want to delete "${policy.name}"?`,
      okText: 'Delete',
      okType: 'danger',
      onOk: () => {
        setPolicies((prev) => prev.filter((p) => p.id !== policy.id));
        message.success('Policy deleted');
      },
    });
  };

  const handleViewStats = (policy: Policy) => {
    setSelectedPolicy(policy);
    setStatsModalVisible(true);
  };

  const handleSave = () => {
    form.validateFields().then((values) => {
      if (selectedPolicy) {
        setPolicies((prev) =>
          prev.map((p) =>
            p.id === selectedPolicy.id
              ? { ...p, ...values, updated_at: new Date().toISOString() }
              : p
          )
        );
        message.success('Policy updated');
      } else {
        const newPolicy: Policy = {
          id: `policy-${Date.now()}`,
          ...values,
          conditions: [],
          actions: [],
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        };
        setPolicies((prev) => [...prev, newPolicy]);
        message.success('Policy created');
      }
      setModalVisible(false);
      form.resetFields();
      setSelectedPolicy(null);
    });
  };

  const columns: ColumnsType<Policy> = [
    {
      title: 'Policy',
      key: 'policy',
      render: (_, record) => (
        <div>
          <Text strong>{record.name}</Text>
          <br />
          <Text type="secondary" style={{ fontSize: 12 }}>
            {record.description}
          </Text>
        </div>
      ),
    },
    {
      title: 'Mode',
      dataIndex: 'mode',
      key: 'mode',
      width: 100,
      render: (mode: PolicyMode) => (
        <Tag color={modeConfig[mode].color}>{modeConfig[mode].text}</Tag>
      ),
    },
    {
      title: 'Enabled',
      dataIndex: 'enabled',
      key: 'enabled',
      width: 80,
      render: (enabled: boolean, record) => (
        <Switch
          checked={enabled}
          onChange={(checked) => handleToggleEnabled(record, checked)}
          size="small"
        />
      ),
    },
    {
      title: 'Accuracy',
      key: 'accuracy',
      width: 150,
      render: (_, record) => {
        const accuracy = record.stats?.accuracy || 0;
        return (
          <Tooltip title={`${record.stats?.true_positives || 0} TP / ${record.stats?.false_positives || 0} FP`}>
            <Progress
              percent={accuracy}
              size="small"
              status={accuracy >= 90 ? 'success' : accuracy >= 75 ? 'normal' : 'exception'}
              format={(percent) => `${percent?.toFixed(1)}%`}
            />
          </Tooltip>
        );
      },
    },
    {
      title: 'Triggers',
      key: 'triggers',
      width: 80,
      render: (_, record) => (
        <Text>{record.stats?.total_triggers || 0}</Text>
      ),
    },
    {
      title: 'Last Triggered',
      key: 'last_triggered',
      width: 140,
      render: (_, record) => (
        <Text type="secondary" style={{ fontSize: 12 }}>
          {record.stats?.last_triggered
            ? dayjs(record.stats.last_triggered).fromNow()
            : 'Never'}
        </Text>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 140,
      render: (_, record) => (
        <Space size="small">
          <Tooltip title="View Stats">
            <Button
              type="text"
              size="small"
              icon={<BarChartOutlined />}
              onClick={() => handleViewStats(record)}
            />
          </Tooltip>
          <Tooltip title="Edit">
            <Button
              type="text"
              size="small"
              icon={<EditOutlined />}
              onClick={() => handleEdit(record)}
            />
          </Tooltip>
          <Tooltip title="Delete">
            <Button
              type="text"
              size="small"
              danger
              icon={<DeleteOutlined />}
              onClick={() => handleDelete(record)}
            />
          </Tooltip>
        </Space>
      ),
    },
  ];

  const stats = {
    total: policies.length,
    enabled: policies.filter((p) => p.enabled).length,
    byMode: {
      observe: policies.filter((p) => p.mode === 'observe').length,
      suggest: policies.filter((p) => p.mode === 'suggest').length,
      auto: policies.filter((p) => p.mode === 'auto').length,
    },
    avgAccuracy:
      policies.reduce((sum, p) => sum + (p.stats?.accuracy || 0), 0) /
      policies.length,
  };

  if (loading) {
    return <LoadingSpinner tip="Loading policies..." />;
  }

  return (
    <div>
      {/* Page Header */}
      <div
        style={{
          marginBottom: 24,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <div>
          <Title level={3} style={{ margin: 0 }}>
            <SafetyCertificateOutlined style={{ marginRight: 12 }} />
            Healing Policies
          </Title>
          <Text type="secondary">
            Configure automated healing rules and actions
          </Text>
        </div>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => {
            setSelectedPolicy(null);
            form.resetFields();
            setModalVisible(true);
          }}
        >
          Create Policy
        </Button>
      </div>

      {/* Stats Row */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={12} sm={6}>
          <Card>
            <Statistic title="Total Policies" value={stats.total} />
          </Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card>
            <Statistic
              title="Enabled"
              value={stats.enabled}
              valueStyle={{ color: '#52c41a' }}
            />
          </Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card>
            <Statistic
              title="Auto Mode"
              value={stats.byMode.auto}
              prefix={<ThunderboltOutlined />}
            />
          </Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card>
            <Statistic
              title="Avg Accuracy"
              value={stats.avgAccuracy.toFixed(1)}
              suffix="%"
              valueStyle={{
                color: stats.avgAccuracy >= 90 ? '#52c41a' : '#faad14',
              }}
            />
          </Card>
        </Col>
      </Row>

      {/* Policies Table */}
      <Card>
        <Table
          columns={columns}
          dataSource={policies}
          rowKey="id"
          pagination={{ pageSize: 10 }}
        />
      </Card>

      {/* Create/Edit Modal */}
      <Modal
        title={selectedPolicy ? 'Edit Policy' : 'Create Policy'}
        open={modalVisible}
        onOk={handleSave}
        onCancel={() => {
          setModalVisible(false);
          form.resetFields();
          setSelectedPolicy(null);
        }}
        width={600}
      >
        <Form form={form} layout="vertical">
          <Form.Item
            name="name"
            label="Policy Name"
            rules={[{ required: true, message: 'Please enter a policy name' }]}
          >
            <Input placeholder="e.g., Auto-Scale Workers" />
          </Form.Item>
          <Form.Item
            name="description"
            label="Description"
            rules={[{ required: true, message: 'Please enter a description' }]}
          >
            <TextArea rows={3} placeholder="Describe what this policy does" />
          </Form.Item>
          <Form.Item
            name="mode"
            label="Mode"
            rules={[{ required: true, message: 'Please select a mode' }]}
          >
            <Select>
              <Select.Option value="observe">
                Observe - Only log, no action
              </Select.Option>
              <Select.Option value="suggest">
                Suggest - Require approval
              </Select.Option>
              <Select.Option value="auto">
                Auto - Execute automatically
              </Select.Option>
            </Select>
          </Form.Item>
          <Form.Item name="enabled" label="Enabled" valuePropName="checked">
            <Switch />
          </Form.Item>
        </Form>
      </Modal>

      {/* Stats Modal */}
      <Modal
        title={`Policy Stats: ${selectedPolicy?.name}`}
        open={statsModalVisible}
        onCancel={() => setStatsModalVisible(false)}
        footer={[
          <Button key="close" onClick={() => setStatsModalVisible(false)}>
            Close
          </Button>,
        ]}
        width={500}
      >
        {selectedPolicy?.stats && (
          <Row gutter={[16, 16]}>
            <Col span={12}>
              <Statistic
                title="Total Triggers"
                value={selectedPolicy.stats.total_triggers}
              />
            </Col>
            <Col span={12}>
              <Statistic
                title="True Positives"
                value={selectedPolicy.stats.true_positives}
                valueStyle={{ color: '#52c41a' }}
              />
            </Col>
            <Col span={12}>
              <Statistic
                title="False Positives"
                value={selectedPolicy.stats.false_positives}
                valueStyle={{ color: '#f5222d' }}
              />
            </Col>
            <Col span={12}>
              <Statistic
                title="Accuracy"
                value={selectedPolicy.stats.accuracy.toFixed(1)}
                suffix="%"
                valueStyle={{
                  color:
                    selectedPolicy.stats.accuracy >= 90
                      ? '#52c41a'
                      : selectedPolicy.stats.accuracy >= 75
                      ? '#faad14'
                      : '#f5222d',
                }}
              />
            </Col>
            <Col span={24}>
              <div style={{ marginTop: 16 }}>
                <Text type="secondary">Accuracy Trend</Text>
                <Progress
                  percent={selectedPolicy.stats.accuracy}
                  strokeColor={{
                    '0%': '#f5222d',
                    '75%': '#faad14',
                    '100%': '#52c41a',
                  }}
                />
              </div>
            </Col>
            <Col span={24}>
              <div>
                <Text type="secondary">False Positive Rate</Text>
                <Progress
                  percent={selectedPolicy.stats.false_positive_rate}
                  status={
                    selectedPolicy.stats.false_positive_rate > 10
                      ? 'exception'
                      : 'normal'
                  }
                  format={(percent) => `${percent?.toFixed(1)}%`}
                />
              </div>
            </Col>
            {selectedPolicy.stats.last_triggered && (
              <Col span={24}>
                <Text type="secondary">
                  Last Triggered:{' '}
                  {dayjs(selectedPolicy.stats.last_triggered).format(
                    'YYYY-MM-DD HH:mm:ss'
                  )}
                </Text>
              </Col>
            )}
          </Row>
        )}
      </Modal>
    </div>
  );
};

export default Policies;
